<template>
  <available-section section="appointment-booking-widget">
    <main class="integration-booking-widget">
      <div class="integration-booking-widget__container">
        <button-back />
        <h1 class="integration-booking-widget__title">Виджет онлайн-записи</h1>
        <p>
          Виджет онлайн-записи на прием позволяет пациентам записываться
          к вам напрямую с сайта 24/7.
        </p>
        <h3 class="integration-booking-widget__subtitle">Для активации виджета:</h3>
        <p>
          1. Подключите SMS-уведомления.<br>
          2. <button
            class="integration-booking-widget__button-recharge"
            type="button"
            v-on:click="onShowBillingInvoiceForm"
          >
            Пополните баланс
          </button>
          для отправки сообщений.<br>
        </p>
        <h3 class="integration-booking-widget__subtitle">Зачем нужны SMS?</h3>
        <p>На номер пациента придет код подтверждения. Это защита от спама и гарантия, что запись
          оставляет реальный человек.
        </p>
        <br>
        <template v-if="hasWidget">
          <p>
            Ссылка на виджет:<br>
            Подходит для размещения в социальных сетях, справочниках и т.д.<br>
          </p>
          <div class="integration-booking-widget__code-wrapper">
            <a v-bind:href="widgetUrl" target="_blank" rel="noopener noreferrer">{{ widgetUrl }}</a>
            <button
              class="integration-booking-widget__button-copy"
              type="button"
              title="Скопировать"
              v-on:click="onCopyWidgetUrl"
            />
          </div>
        </template>
        <h3 class="integration-booking-widget__subtitle">Настройки виджета:</h3>
        <form
          v-if="isLoaded"
          class="integration-booking-widget__connection"
          v-on:submit.prevent="onSubmit"
        >
          <the-select
            v-model="buttonType"
            v-bind:options="buttonTypeOptions"
            title="Тип кнопки вызова виджета"
            v-bind:optional="false"
          />
          <template v-if="!isInlineButtonType">
            <the-select
              v-model="buttonLocation"
              v-bind:options="buttonLocationOptions"
              title="Расположение"
              v-bind:optional="false"
            />
            <input-text v-model="buttonTitle" required title="Название кнопки" />
            <input-number
              v-model="buttonSize"
              required
              native-min-max-validation
              title="Размер кнопки, px"
              v-bind:min="$options.minButtonSize"
              v-bind:max="$options.maxButtonSize"
            />
            <popover-menu overflow-auto>
              <template v-slot:trigger="{ listenerObject }">
                <button
                  class="integration-booking-widget__color"
                  type="button"
                  v-on="listenerObject"
                >
                  Цвет кнопки:
                  <span
                    class="integration-booking-widget__color__preview"
                    v-bind:style="{ backgroundColor: buttonColor }"
                  />
                </button>
              </template>
              <color-picker
                v-bind:initial-color="buttonColor"
                v-on:change="onChangeColor"
              />
            </popover-menu>
          </template>
          <input-phone
            required
            title="Телефон для записи"
            v-model="clinicPhoneNumber"
            v-bind:violations="clinicPhoneNumberInputErrors"
          >
            <template v-slot:after-label>
              <help-icon
                type-icon="question"
                v-bind:tooltip-text="$options.phoneTooltipText"
              />
            </template>
          </input-phone>
          <button
            class="integration-booking-widget__button"
            type="submit"
          >
            Сохранить
          </button>
          <div class="integration-booking-widget__instructions-init" v-if="hasWidget">
            <div class="integration-booking-widget__step">
              <p>
                Шаг 1. В тег <code>&lt;head&gt;</code> на странице вашего сайта
                добавьте подключение виджета:
              </p>
              <div class="integration-booking-widget__code-wrapper">
                <code ref="connectWidgetCode">&lt;script async src="{{ widgetSrc }}"&gt;&lt;/script&gt;</code>
                <button
                  class="integration-booking-widget__button-copy"
                  type="button"
                  title="Скопировать"
                  v-on:click="onCopyConnectWidgetCode"
                />
              </div>
            </div>
            <div class="integration-booking-widget__step">
              <p>
                Шаг 2. В тело страницы сайта добавьте код для инициализации виджета:
              </p>
              <div class="booking-widget-code integration-booking-widget__code">
                <code ref="initializeWidgetCode">
                  <pre>
&lt;script&gt;
{{ widgetCode }}
&lt;/script&gt;</pre>
                </code>
                <div class="booking-widget-code__buttons">
                  <button
                    class="integration-booking-widget__button-preview"
                    type="button"
                    title="Предпросмотр"
                    v-on:click="onPreviewWidget"
                  />
                  <button
                    class="integration-booking-widget__button-copy"
                    type="button"
                    title="Скопировать"
                    v-on:click="onCopyInitializeWidgetCode"
                  />
                </div>
              </div>
            </div>
            <div v-if="isInlineButtonType" class="integration-booking-widget__step">
              <p>
                Шаг 3. В свою кнопку добавьте вызов виджета:<br>
                Например, так:
              </p>
              <div class="integration-booking-widget__code-wrapper">
                <code ref="buttonWidgetCode">
                  &lt;button onclick="openBooking()"&gt;Записаться на прием&lt;/button&gt;
                </code>
                <button
                  class="integration-booking-widget__button-copy"
                  type="button"
                  title="Скопировать"
                  v-on:click="onCopyButtonWidgetCode"
                />
              </div>
            </div>
          </div>
        </form>
        <section class="integration-booking-widget__control" v-if="hasWidget">
          <h3 class="integration-booking-widget__subtitle">Настройка списка врачей</h3>
          <p>Отметьте врачей, к которым будет доступна онлайн-запись на сайте</p>
          <div class="integration-booking-widget__table">
            <div class="integration-booking-widget__table__head">
              <div class="integration-booking-widget__table__cell">№</div>
              <div class="integration-booking-widget__table__cell">ФИО врача</div>
              <div class="integration-booking-widget__table__cell align">Выводить в виджете</div>
            </div>
            <div
              class="integration-booking-widget__table__row"
              v-for="employee in employees"
              v-bind:key="employee.id"
            >
              <div data-label="№" class="integration-booking-widget__table__cell number" />
              <div data-label="ФИО врача" class="integration-booking-widget__table__cell">
                {{ employee.name }}
              </div>
              <div data-label="Интеграция" class="integration-booking-widget__table__cell align">
                <input
                  aria-label="Включить в интеграцию"
                  type="checkbox"
                  class="integration-booking-widget__checkbox"
                  v-bind:checked="employee.is_integrated"
                  v-on:change="onSwitchEmployee(employee.id, $event)"
                  :id="`checkbox-integration${employee.id}`"
                />
                <label v-bind:for="`checkbox-integration${employee.id}`" />
              </div>
            </div>
          </div>
        </section>
        <support-contacts
          class="integration-booking-widget__contacts"
          title="Если у вас возникли вопросы, свяжитесь с нами:"
        />
      </div>
      <side-slide-transition>
        <side-dialog
          v-if="isShowBillingInvoiceForm"
          v-click-outside="closeBillingInvoiceDialogForm"
          v-on:cancel="closeBillingInvoiceDialogForm"
          label="Продлить тариф">
          <form-billing-invoice
            without-tariff
            v-bind:is-await-request="isBillingInvoiceAwaitRequest"
            v-bind:module-options="billingInvoiceModules"
            v-bind:packet-module-options="billingInvoicePacketModules"
            v-bind:preselected-modules="selectedBillingInvoiceModules"
            v-bind:tariff-options="billingInvoiceTariffs"
            v-on:download="onDownloadBillingInvoice"
          />
        </side-dialog>
      </side-slide-transition>
    </main>
  </available-section>
</template>
<script>
import AvailableSection from '@/views/ClinicPanel/AvailableSection.vue';
import ButtonBack from '@/components/ButtonBack.vue';
import ColorPicker from '@/components/ColorPicker.vue';
import FormBillingInvoice from '@/components/FormBillingInvoice.vue';
import HelpIcon from '@/components/HelpIcon.vue';
import InputNumber from '@/components/InputNumber.vue';
import InputPhone from '@/components/InputPhone.vue';
import InputText from '@/components/InputText.vue';
import PopoverMenu from '@/components/PopoverMenu.vue';
import SideDialog from '@/components/SideDialog.vue';
import SideSlideTransition from '@/components/SideSlideTransition.vue';
import SupportContacts from '@/components/SupportContacts.vue';
import TheSelect from '@/components/TheSelect.vue';
import aboutMixin from '@/mixins/aboutMixin';
import billingInvoiceMixin from '@/mixins/billingInvoiceMixin';
import clipboardMixin from '@/mixins/clipboardMixin';
import notifierMixin from '@/mixins/notifierMixin';
import saveFileMixin from '@/mixins/saveFileMixin';

/**
 * @description Разрешить получение списка ошибок для поля ввода.
 * @param {string} cause Имя поля ввода.
 * @returns {function}
 */
const resolveInputErrors = (cause) => (vm) => (
  cause in vm.inputErrors ? vm.inputErrors[cause] : []
);

/**
 * @description Тип кнопки встроенная.
 * @type {string}
 */
const BUTTON_TYPE_INLINE = 'inline';

/**
 * @description Сгенерировать код инициализации виджета.
 * @param {string} widgetId
 * @param {string} buttonType
 * @param {string} buttonLocation
 * @param {string} buttonTitle
 * @param {string} buttonSize
 * @param {string} buttonColor
 * @returns {string}
 */
const generateBookingWidgetInitializeScript = (
  widgetId,
  buttonType,
  buttonLocation,
  buttonTitle,
  buttonSize,
  buttonColor,
) => {
  let config = '';
  let buttonFunction = '';
  if (buttonType === BUTTON_TYPE_INLINE) {
    config = `const config = {
      widgetId: '${widgetId}',
      buttonType: '${buttonType}',
    };`;
    buttonFunction = `

  function openBooking() {
    window['BookMis32Top'].push(['openModal']);
  }`;
  } else {
    config = `const config = {
      widgetId: '${widgetId}',
      buttonType: '${buttonType}',
      buttonLocation: '${buttonLocation}',
      buttonTitle: '${buttonTitle}',
      buttonSize: ${buttonSize},
      buttonColor: '${buttonColor}',
    };`;
  }
  return `  (function() {
    ${config}
    const widgetName = 'BookMis32Top';
    window[widgetName] = window[widgetName] || [];
    window[widgetName].push(['init', config]);
  })();${buttonFunction}`;
};

/**
 * @description Компонент интеграции с виджетом онлайн-записи.
 * @type {Vue}
 */
export default {
  phoneTooltipText: `Если код подтверждения не придет по звонку или SMS, пациент увидит номер клиники.
  По нему он сможет позвонить и записаться самостоятельно`,
  maxButtonSize: 50,
  minButtonSize: 16,
  components: {
    AvailableSection,
    ButtonBack,
    ColorPicker,
    FormBillingInvoice,
    HelpIcon,
    InputNumber,
    InputPhone,
    InputText,
    PopoverMenu,
    SideDialog,
    SideSlideTransition,
    SupportContacts,
    TheSelect,
  },
  computed: {
    /**
     * @description Имеет виджет.
     * @returns {boolean}
     */
    hasWidget() {
      return !!this.widgetId;
    },
    widgetUrl() {
      if (this.hasWidget !== true) {
        return '';
      }
      const parts = this.widgetSrc.split('/');
      const url = ['https://'];
      if (parts.length > 0) {
        url.push(parts[2], '/', this.widgetId);
      }
      return url.join('');
    },
    clinicPhoneNumberInputErrors: resolveInputErrors('clinic-phone-number'),
    isInlineButtonType() {
      return this.buttonType === BUTTON_TYPE_INLINE;
    },
    widgetCode() {
      const bodyScript = generateBookingWidgetInitializeScript(
        this.widgetId,
        this.buttonType,
        this.buttonLocation,
        this.buttonTitle,
        this.buttonSize,
        this.buttonColor,
      );
      return bodyScript;
    },
  },
  created() {
    this.fetchWidget();
  },
  beforeDestroy() {
    if (this.$bookingWidget.initialized()) {
      this.$bookingWidget.destroy();
    }
  },
  data() {
    return {
      /**
       * @description Цвет кнопки.
       * @type {string}
       */
      buttonColor: '',
      /**
       * @description Расположение.
       * @type {string}
       */
      buttonLocation: '',
      /**
       * @description Опции расположения виджета.
       * @type {any[]}
       */
      buttonLocationOptions: [],
      /**
       * @description Размер кнопки.
       * @type {number}
       */
      buttonSize: 0,
      /**
       * @description Текст кнопки.
       * @type {string}
       */
      buttonTitle: '',
      /**
       * @description Тип кнопки вызова виджета.
       * @type {string}
       */
      buttonType: '',
      /**
       * @description Опции типа кнопки вызова виджета.
       * @type {any[]}
       */
      buttonTypeOptions: [],
      /**
       * @description Номер телефона клиники.
       * @type {string}
       */
      clinicPhoneNumber: '',
      /**
       * @description Сотрудники.
       * @type {any[]}
       */
      employees: [],
      /**
       * @description Нарушения ограничений.
       * @type {any}
       */
      inputErrors: {},
      /**
       * @description Идентификатор виджета.
       * @type {string}
       */
      widgetId: '',
      /**
       * @description Адрес виджета.
       * @type {string}
       */
      widgetSrc: '',
      /**
       * @description Данные загружены.
       * @type {boolean}
       */
      isLoaded: false,
    };
  },
  methods: {
    /**
     * @description Обработать создание виджета.
     * @returns {void}
     */
    createWidget() {
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
        this.inputErrors = error.allCauses();
      };
      const onSuccess = ({ message, widgetId }) => {
        this.notifySuccess(message);
        this.inputErrors = {};
        this.widgetId = widgetId;
        this.fetchWidget();
      };
      this
        .$appointmentBookingWidgetGateway
        .createWidget(this.makeRequest())
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Обработать редактирование виджета.
     * @returns {void}
     */
    editWidget() {
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
        this.inputErrors = error.allCauses();
      };
      const onSuccess = ({ message }) => {
        this.notifySuccess(message);
        this.inputErrors = {};
        this.isShowEditSmsRuApiKey = false;
      };
      this
        .$appointmentBookingWidgetGateway
        .editWidget(this.makeRequest())
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Получить виджет.
     * @returns {void}
     */
    fetchWidget() {
      const onSuccess = ({ widget, options }) => {
        this.buttonColor = widget.button_color;
        this.buttonLocation = widget.button_location;
        this.buttonLocationOptions = options.button_locations;
        this.buttonSize = widget.button_size;
        this.buttonTitle = widget.button_title;
        this.buttonType = widget.button_type;
        this.buttonTypeOptions = options.button_types;
        this.clinicPhoneNumber = widget.clinic_phone_number;
        this.employees = options.employees;
        this.widgetSrc = widget.widget_src;
        this.widgetId = widget.id;
      };
      this
        .$appointmentBookingWidgetGateway
        .viewWidget()
        .then((result) => result.fold(this.notifyFail, onSuccess))
        .finally(() => { this.isLoaded = true; });
    },
    /**
     * @description Сформировать запрос.
     * @returns {any}
     */
    makeRequest() {
      return {
        button_color: this.buttonColor,
        button_location: this.buttonLocation,
        button_size: this.buttonSize,
        button_title: this.buttonTitle,
        button_type: this.buttonType,
        clinic_phone_number: this.clinicPhoneNumber,
      };
    },
    /**
     * @description Обработать изменение цвета кнопки.
     * @returns {void}
     */
    onChangeColor(color) {
      this.buttonColor = color;
    },
    /**
     * @description Обработать копирование кода подключения виджета.
     * @returns {void}
     */
    onCopyConnectWidgetCode() {
      this.toClipboard(this.$refs.connectWidgetCode.textContent).then(
        () => { this.notifySuccess('Код подключения виджета скопирован'); },
        () => { this.notifyFail('Не удалось скопировать код подключения виджета'); },
      );
    },
    /**
     * @description Обработать копирование кода инициализации виджета.
     * @returns {void}
     */
    onCopyInitializeWidgetCode() {
      this.toClipboard(this.$refs.initializeWidgetCode.textContent).then(
        () => { this.notifySuccess('Код инициализации виджета скопирован'); },
        () => { this.notifyFail('Не удалось скопировать код инициализации виджета'); },
      );
    },
    /**
     * @description Обработать копирование кода инициализации виджета.
     * @returns {void}
     */
    onCopyButtonWidgetCode() {
      this.toClipboard(this.$refs.buttonWidgetCode.textContent).then(
        () => { this.notifySuccess('Код кнопки скопирован'); },
        () => { this.notifyFail('Не удалось скопировать код кнопки'); },
      );
    },
    onCopyWidgetUrl() {
      this.toClipboard(this.widgetUrl).then(
        () => { this.notifySuccess('Ссылка на виджет скопирована'); },
        () => { this.notifyFail('Не удалось скопировать ссылку на виджет'); },
      );
    },
    /**
     * @description Обработать отправку формы.
     * @returns {void}
     */
    onSubmit() {
      if (this.hasWidget) {
        this.editWidget();
      } else {
        this.createWidget();
      }
    },
    /**
     * @description Обработать переключение включенности сотрудника.
     * @param {number} employeeId Идентификатор сотрудника.
     * @param {Event}  event      Событие.
     */
    onSwitchEmployee(employeeId, event) {
      const onResponse = (result) => {
        result.forEach(({ title }) => {
          this.notifySuccess(title);
        });
        result.forEachLeft(({ title }) => {
          this.notifyFail(title);
          this.employees = [];
          this.fetchWidget();
        });
      };
      const isEnabled = event.target.checked;
      if (isEnabled) {
        this
          .$appointmentBookingWidgetGateway
          .enableEmployee(employeeId)
          .then(onResponse);
      } else {
        this
          .$appointmentBookingWidgetGateway
          .disableEmployee(employeeId)
          .then(onResponse);
      }
    },
    /**
     * @description Обработать предпросмотр виджета.
     * @returns {void}
     */
    onPreviewWidget() {
      const params = {
        widgetId: this.widgetId,
        buttonType: BUTTON_TYPE_INLINE,
        buttonLocation: this.buttonLocation,
        buttonTitle: this.buttonTitle,
        buttonSize: this.buttonSize,
        buttonColor: this.buttonColor,
      };
      this.$bookingWidget.open(this.widgetSrc, params);
    },
    preselectDefaultBillingOptions() {
      this.selectedBillingInvoiceModules = this
        .billingInvoicePacketModules
        .filter(({ id }) => id === 'sms')
        .map((packet) => ({
          cost: packet.cost,
          count: packet.initial_count,
          isPacketModule: true,
          selectedTariff: packet,
        }));
    },
  },
  mixins: [
    aboutMixin,
    billingInvoiceMixin,
    clipboardMixin,
    notifierMixin,
    saveFileMixin,
  ],
  name: 'ClinicPanelAppointmentBookingWidget',
};
</script>
<style lang="scss">
@use "@/scss/copy-icon";
@use "@/scss/buttons";

.integration-booking-widget {
  width: 100%;
  height: 100%;
  overflow: scroll;
  &__container {
    padding: 38px 20px 200px 23px;
  }
  &__contacts {
    // border-top: 1px solid #C2CFE0;
    margin-top: 46px;
  }
  &__title {
    font-weight: 600;
    font-size: 38px;
    line-height: 46px;
    color: #000000;
    margin: 0 0 22px 0;
  }
  &__subtitle {
    color: #000000;
    margin: 22px 0 10px;
  }
  &__connection {
    background-color: var(--section-background-color);
    padding: 30px 50px 35px 40px;
    margin: 10px 0 0 0;
    display: flex;
    flex-direction: column;
    gap: 7px;
  }
  &__definition {
    &__label {
      font-weight: 400;
      font-size: 12px;
      line-height: 16px;
      color: var(--unfocus-color);
    }
    &__count {
      font-weight: 400;
      font-size: 16px;
      line-height: 19px;
      color: #192A3E;
      margin: 10px 0 30px 0;
    }
    &__key {
      display: flex;
      align-items: center;
      background-color: var(--primary-background-color);
      border: 1px solid var(--border-color);
      border-radius: 4px;
      padding: 12px 45px 12px 20px;
      position: relative;
      font-size: 21px;
      width: 100%;
    }
    &__copy {
      @extend %copy-icon;
      position: absolute;
      width: 24px;
      height: 24px;
      right: 20px;
    }
  }
  &__instructions-init {
    display: grid;
    row-gap: 15px;
    padding-top: 15px;
  }
  &__step {
    display: grid;
    row-gap: 5px;
  }
  &__code-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  &__code {
    position: relative;
  }
  &__button {
    @include buttons.btn-blue(244px, 48px, 16px);
    max-width: 100%;
    margin: 20px 0 0 0;
    &:disabled {
      background-color: var(--unfocus-color);
    }
    &-copy {
      flex-shrink: 0;
      width: 18px;
      height: 18px;
      background: url("@/assets/img/copy-grey.svg") no-repeat center;
      background-size: contain;
      transition: background-image 0.3s;
      transform: scale(-1, 1);
      &:hover {
        background-image: url("@/assets/img/copy-blue.svg");
      }
    }
    &-preview {
      flex-shrink: 0;
      width: 18px;
      height: 18px;
      mask: url("@/assets/img/eye-open-black.svg") no-repeat center;
      mask-size: contain;
      background-color: var(--action-color);
      transition: background-image 0.3s;
      &:hover {
        background-color: var(--action-color);
      }
    }
    &-recharge {
      color: var(--action-color);
      text-decoration: underline;
    }
  }
  &__color {
    vertical-align: middle;
    display: flex;
    align-items: center;
    gap: 10px;
    &__preview {
      display: inline-block;
      width: 40px;
      height: 40px;
      border-radius: 50%;
    }
  }
  &__table {
    counter-reset: counterEmployees;
    background: #FFFFFF;
    margin: 5px 0 0 0;
    &__head,
    &__row {
      align-items: center;
      cursor: pointer;
      display: grid;
      font-size: 14px;
      grid-template-columns: 40px 1fr 158px;
      line-height: 17px;
    }
    &__head {
      font-weight: 700;
      border-bottom: 2px solid #0489E0;
      padding: 10px 0;
    }
    &__row {
      border-bottom: 1px solid #EAEFF3;
      padding: 12px 0;
      &:hover {
        background: #EAEFF3;
      }
    }
    &__cell {
      padding: 0 8px;
      position: relative;
      &.number:after {
        counter-increment: counterEmployees;
        content: counter(counterEmployees);
      }
      &.align {
        text-align: center;
      }
    }
  }
  &__checkbox {
    position: absolute;
    z-index: -1;
    opacity: 0;
    &:checked + label:before {
      background: rgba(4, 137, 224, 0.18);
      border: 1px solid #0489E0;
    }
    &:checked + label:after {
      left: 16px;
      background: #0489E0;
    }
  }
  &__checkbox + label {
  position: relative;
  padding: 0 0 0 38px;
  cursor: pointer;
  }
  &__checkbox + label:before {
    content: '';
    position: absolute;
    top: -4px;
    left: 0;
    width: 38px;
    height: 24px;
    border-radius: 15px;
    background: #EAEFF3;
    border: 1px solid #C2CFE0;
    transition: .2s;
  }
  &__checkbox + label:after {
    content: '';
    position: absolute;
    top: -2px;
    left: 2px;
    width: 20px;
    height: 20px;
    border-radius: 10px;
    background: #90A0B7;
    transition: .2s;
  }
  &__control {
    border-top: 1px solid #C2CFE0;
    background-color: var(--section-background-color);
    padding: 30px 50px 60px 40px;
  }
  p {
    display: block;
  }
  code {
    display: inline-block;
    width: fit-content;
    padding: 0.3em 0.6em;
  }
  pre {
    white-space: break-spaces;
  }
}

.booking-widget-code {
  width: fit-content;
  &__buttons {
    position: absolute;
    top: 10px;
    right: 10px;
    display: flex;
    align-items: center;
    column-gap: 6px;
  }
}

@media screen and (max-width: 1240px) {
  .integration {
    &__container {
      width: 100%;
      padding: 45px 20px 145px;
    }
  }
}
@media screen and (max-width: 640px) {
  .integration {
    &__container {
      padding: 45px 10px 145px;
    }
  }
}
@media screen and (max-width: 480px) {
  .integration {
    &__connection {
      background-color: transparent;
      padding: 0 0 20px 0;
    }
  }
}
</style>
